/**
 * useActivePollsCount — compteur de sondages actifs pour l'utilisateur courant.
 *
 * Renvoie le compte + le plafond + un flag `isAtLimit`. Sert principalement
 * à afficher un badge "X/10" dans le dashboard FREE et à désactiver
 * préemptivement le bouton "Créer un sondage" quand le plafond est atteint.
 *
 * Écoute l'événement `pollCreated` (émis par `usePolls.createPoll`) et
 * `pollArchived` / `pollDeleted` (à émettre par les actions correspondantes)
 * pour rafraîchir la valeur.
 *
 * Comportement :
 * - ANONYMOUS / SOLO → `max = null` (illimité), `isAtLimit = false`.
 * - FREE             → `max = 10`, `isAtLimit = count >= 10`.
 * - Si Supabase est indisponible, le hook retourne `count = null` et
 *   `isAtLimit = false` (fail-open : on n'empêche pas la création).
 */

import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { pollService } from "../services/PollService";
import {
  determineUserPlan,
  getMaxActivePolls,
  hasPaidAccess,
  type PlanId,
} from "@core/constants/quotas";

export interface ActivePollsCountResult {
  count: number | null;
  max: number | null;
  isAtLimit: boolean;
  isNearLimit: boolean;
  plan: PlanId;
  /** Rafraîchit manuellement le compteur. */
  refresh: () => Promise<void>;
}

export function useActivePollsCount(): ActivePollsCountResult {
  const { user, profile } = useAuth();
  const plan = determineUserPlan(!!user, hasPaidAccess(profile));
  const max = getMaxActivePolls(plan);
  const [count, setCount] = useState<number | null>(null);

  const fetchCount = useCallback(async () => {
    if (!user?.id || max === null) {
      // Pas de plafond → pas besoin de compter.
      setCount(null);
      return;
    }
    try {
      const n = await pollService.countActivePollsByUser(user.id);
      setCount(n);
    } catch {
      setCount(null);
    }
  }, [user?.id, max]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => {
      fetchCount();
    };
    window.addEventListener("pollCreated", handler);
    window.addEventListener("pollArchived", handler);
    window.addEventListener("pollDeleted", handler);
    return () => {
      window.removeEventListener("pollCreated", handler);
      window.removeEventListener("pollArchived", handler);
      window.removeEventListener("pollDeleted", handler);
    };
  }, [fetchCount]);

  const isAtLimit = count !== null && max !== null && count >= max;
  const isNearLimit =
    count !== null && max !== null && !isAtLimit && count / max >= 0.8;

  return { count, max, isAtLimit, isNearLimit, plan, refresh: fetchCount };
}
